import { SuperEmojiType, SuperEmojiCollection, Position, CapturedEmoji } from '../utils/types';

// Configuration for each super emoji
export const SUPER_EMOJIS: SuperEmojiType[] = [
  {
    emoji: '💃',
    name: 'Dance Destroyer',
    powerType: 'dance',
    description: 'Bounces at 5 random points, destroying UFOs',
    duration: 3,
    count: 5,
    radius: 80
  },
  {
    emoji: '⚔️',
    name: 'Sword Slasher',
    powerType: 'sword',
    description: 'Two spinning swords destroy UFOs in their path',
    duration: 3,
    count: 2,
    radius: 60
  },
  {
    emoji: '🪂',
    name: 'Parachute Storm',
    powerType: 'parachute',
    description: '5 parachutes fall from sky destroying UFOs',
    duration: 3,
    count: 5,
    radius: 70
  },
  {
    emoji: '💣',
    name: 'Bomb Blast',
    powerType: 'bomb',
    description: 'Destroys all UFOs currently on screen',
    radius: 9999
  },
  {
    emoji: '❤️‍🔥',
    name: 'Fire Heart',
    powerType: 'fire',
    description: 'Destroys all UFOs and completes level',
    radius: 9999
  }
];

class SuperEmojiService {
  private static instance: SuperEmojiService;

  private constructor() {}

  static getInstance(): SuperEmojiService {
    if (!SuperEmojiService.instance) {
      SuperEmojiService.instance = new SuperEmojiService();
    }
    return SuperEmojiService.instance;
  }

  shouldSpawnSuperEmoji(): boolean {
    // 10% chance for better visibility during testing
    return Math.random() < 0.1;
  }

  getRandomSuperEmoji(): string {
    return SUPER_EMOJIS[Math.floor(Math.random() * SUPER_EMOJIS.length)].emoji;
  }

  isSuperEmoji(emoji: string): boolean {
    return SUPER_EMOJIS.some(superEmoji => superEmoji.emoji === emoji);
  }

  getSuperEmojiConfig(emoji: string): SuperEmojiType | undefined {
    return SUPER_EMOJIS.find(superEmoji => superEmoji.emoji === emoji);
  }

  addToCollection(collection: SuperEmojiCollection, emoji: string): SuperEmojiCollection {
    return {
      ...collection,
      [emoji]: (collection[emoji] || 0) + 1
    };
  }

  useFromCollection(collection: SuperEmojiCollection, emoji: string): SuperEmojiCollection | null {
    const count = collection[emoji] || 0;
    if (count <= 0) return null;

    return {
      ...collection,
      [emoji]: count - 1
    };
  }

  getUFOsInRadius(ufos: CapturedEmoji[], center: Position, radius: number): CapturedEmoji[] {
    return ufos.filter(ufo => {
      if (ufo.status !== 'captured') return false;
      
      const distance = Math.sqrt(
        Math.pow(ufo.position.x - center.x, 2) + 
        Math.pow(ufo.position.y - center.y, 2)
      );
      return distance <= radius;
    });
  }

  generateBouncePoints(windowSize: { width: number; height: number }, count: number): Position[] {
    const points: Position[] = [];
    for (let i = 0; i < count; i++) {
      points.push({
        x: Math.random() * windowSize.width,
        y: Math.random() * windowSize.height,
        rotation: 0
      });
    }
    return points;
  }

  generateSwordTrajectories(windowSize: { width: number; height: number }): Position[][] {
    return [
      [
        { x: 0, y: windowSize.height, rotation: 45 },
        { x: windowSize.width, y: 0, rotation: 45 }
      ],
      [
        { x: windowSize.width, y: windowSize.height, rotation: -45 },
        { x: 0, y: 0, rotation: -45 }
      ]
    ];
  }

  generateParachutePositions(windowSize: { width: number; height: number }, count: number): Position[] {
    const positions: Position[] = [];
    const spacing = windowSize.width / (count + 1);
    
    for (let i = 0; i < count; i++) {
      positions.push({
        x: spacing * (i + 1),
        y: 0, // Start at top of visible screen instead of -50
        rotation: 0
      });
    }
    return positions;
  }
}

export const superEmojiService = SuperEmojiService.getInstance();